import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import storeService from '../../services/storeService';
import { useStore } from '../../context/StoreContext';
import { setOtaEnabled as applyOtaEnabledToDevice } from '../../services/ota/otaSettings';
import { setBusinessHours as applyBusinessHoursToDevice } from '../../services/ota/otaWindow';

const TimeSettings = () => {
  const [hours, setHours] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [otaEnabled, setOtaEnabled] = useState(true);
  const [otaSaving, setOtaSaving] = useState(false);
  const [otaError, setOtaError] = useState('');
  const { id: storeId } = useParams();
  const { activeStore } = useStore();
  // Only push changes to the live OTA module if we're editing THIS device's
  // active store — otherwise editing another store would clobber its policy.
  const isActiveStore = Boolean(activeStore?._id) && activeStore._id === storeId;

  useEffect(() => {
    if (storeId && storeId !== 'setup') {
      storeService.getStoreById(storeId)
        .then(data => {
          const dayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
          const sortedHours = (data?.openingHours || []).sort((a, b) => dayOrder.indexOf(a.dayOfWeek) - dayOrder.indexOf(b.dayOfWeek));
          setHours(sortedHours);
          setOtaEnabled(data?.otaUpdatesEnabled !== false);
        })
        .catch(err => {
          console.error('Failed to load opening hours', err);
        })
        .finally(() => setIsLoading(false));
    }
  }, [storeId]);

  const handleHourChange = (id, field, value) => {
    setHours(currentHours => currentHours.map(h => h._id === id ? { ...h, [field]: value } : h));
  };

  const handleSave = async () => {
    await storeService.updateOpeningHours(storeId, hours);
    // Keep the live OTA window in sync so a bundle can't apply during the newly
    // saved opening hours on this device.
    if (isActiveStore) {
      applyBusinessHoursToDevice({ timezone: activeStore.timezone, openingHours: hours });
    }
    setSuccess('Opening hours saved successfully!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleOtaToggle = async (checked) => {
    setOtaSaving(true);
    setOtaError('');
    try {
      await storeService.updateStore(storeId, { otaUpdatesEnabled: checked });
      setOtaEnabled(checked);
      // Apply immediately to the running scheduler on this device.
      if (isActiveStore) {
        applyOtaEnabledToDevice(checked);
      }
      setSuccess(`Automatic app updates ${checked ? 'enabled' : 'disabled'} for this store.`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setOtaError(err.response?.data?.message || 'Failed to update app-update setting.');
    } finally {
      setOtaSaving(false);
    }
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <div>
      {success && <p className="bg-green-100 text-green-700 p-3 rounded mb-4">{success}</p>}

      <div className="mb-6 p-4 border rounded-lg bg-gray-50">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold">Automatic App Updates</h2>
            <p className="text-sm text-gray-500 mt-1">
              When enabled, this store&rsquo;s POS devices download and apply new app versions
              over the air. Updates only apply while a device is idle and the store is closed
              (outside the opening hours below), so they never interrupt a sale.
            </p>
          </div>
          <label className="flex items-center gap-2 shrink-0">
            <input
              type="checkbox"
              checked={otaEnabled}
              onChange={(e) => handleOtaToggle(e.target.checked)}
              disabled={otaSaving}
              className="h-4 w-4"
            />
            <span className="text-sm font-medium">{otaEnabled ? 'Enabled' : 'Disabled'}</span>
          </label>
        </div>
        {otaError && <p className="text-xs text-red-600 mt-2">{otaError}</p>}
      </div>

      <h2 className="text-xl font-bold mb-4">Opening Hours</h2>
      <div className="space-y-3">
        {hours.map(h => (
          <div key={h._id} className="grid grid-cols-5 gap-4 items-center p-2 border-b">
            <span className="font-semibold col-span-2">{h.dayOfWeek}</span>
            <input type="time" value={h.startTime} disabled={!h.isOpen} onChange={e => handleHourChange(h._id, 'startTime', e.target.value)} className="p-1 border rounded-md disabled:bg-gray-100" />
            <input type="time" value={h.endTime} disabled={!h.isOpen} onChange={e => handleHourChange(h._id, 'endTime', e.target.value)} className="p-1 border rounded-md disabled:bg-gray-100" />
            <label className="flex items-center justify-end"><input type="checkbox" checked={h.isOpen} onChange={e => handleHourChange(h._id, 'isOpen', e.target.checked)} className="mr-2 h-4 w-4" /> Open</label>
          </div>
        ))}
      </div>
      <div className="flex justify-end mt-6">
        <button onClick={handleSave} className="bg-primary text-white px-6 py-2 rounded-md">Save Hours</button>
      </div>
    </div>
  );
};

export default TimeSettings;
